import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Package, Calendar, AlertTriangle } from "lucide-react";
import { formatDate } from "@/utils/dateUtils";
import { format, differenceInDays } from "date-fns";

export default function AlertsModal({ isOpen, onClose, type, items, onReorder }) {
  const getExpirationStatus = (expirationDate) => {
    const daysUntilExpiration = differenceInDays(
      new Date(expirationDate),
      new Date()
    );
    if (daysUntilExpiration < 0)
      return { status: "expired", color: "text-red-600", label: "Expired" };
    if (daysUntilExpiration <= 7)
      return {
        status: "critical",
        color: "text-red-600",
        label: `${daysUntilExpiration} days`,
      };
    if (daysUntilExpiration <= 30)
      return {
        status: "warning",
        color: "text-orange-600",
        label: `${daysUntilExpiration} days`,
      };
    return {
      status: "normal",
      color: "text-slate-600",
      label: `${daysUntilExpiration} days`,
    };
  };

  const renderLowStockContent = () => (
    <div className="space-y-3 max-h-[500px] overflow-y-auto pr-2">
      {items.map((medicine) => (
        <div
          key={medicine.medicineId}
          className="p-4 bg-red-50/60 rounded-lg border border-red-200/60"
        >
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-start gap-3 flex-1 min-w-0">
              <div className="p-2 rounded-lg bg-red-100 flex-shrink-0 mt-1">
                <Package className="w-5 h-5 text-red-600" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-slate-900 mb-2">
                  {medicine.name}
                </div>

                {/* Stock Status */}
                <div className="flex items-center gap-3 text-sm mb-2">
                  <span className={`font-medium ${medicine.totalStock === 0 ? "text-red-700" : "text-slate-900"}`}>
                    {medicine.totalStock} pack{medicine.totalStock !== 1 ? "s" : ""} remaining
                  </span>
                  <span className="text-slate-400">•</span>
                  <span className="text-orange-600">
                    Min: {medicine.minimumStock}
                  </span>
                  {medicine.batchCount > 1 && (
                    <>
                      <span className="text-slate-400">•</span>
                      <span className="text-slate-500">
                        {medicine.batchCount} batch{medicine.batchCount !== 1 ? "es" : ""}
                      </span>
                    </>
                  )}
                </div>

                {/* Batch Details */}
                {medicine.batches && medicine.batches.length > 0 && (
                  <div className="space-y-1.5 mt-3 pt-3 border-t border-red-200">
                    <div className="text-xs font-medium text-slate-600 uppercase">Batches:</div>
                    {medicine.batches.map((batch) => (
                      <div key={batch.id} className="flex items-center gap-2 text-sm">
                        <span className="text-xs bg-slate-100 text-slate-700 px-2 py-0.5 rounded font-medium">
                          {batch.batchNumber}
                        </span>
                        <span className="text-slate-600">{batch.currentStock} packs</span>
                        {batch.expiryDate && (
                          <>
                            <span className="text-slate-400">•</span>
                            <span className="text-xs text-slate-500">
                              Exp: {formatDate(batch.expiryDate, "MMM yyyy")}
                            </span>
                          </>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <Button
              size="sm"
              variant="outline"
              onClick={() => {
                onReorder(medicine);
                onClose();
              }}
              className="border-red-300 text-red-700 hover:bg-red-50 flex-shrink-0"
            >
              Reorder
            </Button>
          </div>
        </div>
      ))}
    </div>
  );

  const renderExpirationContent = () => (
    <div className="space-y-3 max-h-[500px] overflow-y-auto pr-2">
      {items.map((item) => {
        const expiration = getExpirationStatus(item.expiryDate);
        return (
          <div
            key={item.id}
            className="p-4 bg-orange-50/60 rounded-lg border border-orange-200/60"
          >
            <div className="flex items-start justify-between gap-3">
              <div className="flex items-start gap-3 flex-1 min-w-0">
                <div className="p-2 rounded-lg bg-orange-100 flex-shrink-0 mt-1">
                  <Package className="w-5 h-5 text-orange-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-slate-900 mb-2">
                    {item.name}
                  </div>

                  {/* Unique Identifiers */}
                  <div className="flex items-center gap-2 flex-wrap mb-2">
                    {item.packSize && (
                      <span className="text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded font-medium">
                        Pack: {item.packSize}
                      </span>
                    )}
                    <span className="text-xs bg-slate-100 text-slate-700 px-2 py-0.5 rounded font-medium">
                      Batch: {item.batchNumber}
                    </span>
                    <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded font-medium">
                      Exp: {format(new Date(item.expiryDate), "MMM d, yyyy")}
                    </span>
                    {item.supplier && (
                      <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded font-medium">
                        {item.supplier}
                      </span>
                    )}
                  </div>

                  {/* Stock Info */}
                  <div className="flex items-center gap-3 text-sm text-slate-600">
                    <span className="font-medium text-slate-900">
                      {item.currentStock} packs in stock
                    </span>
                    {item.storageLocation && (
                      <>
                        <span className="text-slate-400">•</span>
                        <span className="text-slate-500">
                          Location: {item.storageLocation}
                        </span>
                      </>
                    )}
                  </div>
                </div>
              </div>

              <div className={`text-sm font-semibold ${expiration.color} flex-shrink-0 whitespace-nowrap`}>
                {expiration.label}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl">
            {type === "lowStock" ? (
              <>
                <AlertTriangle className="w-5 h-5 text-red-600" />
                <span className="text-red-700">Low Stock Alerts ({items.length})</span>
              </>
            ) : (
              <>
                <Calendar className="w-5 h-5 text-orange-600" />
                <span className="text-orange-700">Expiration Alerts ({items.length})</span>
              </>
            )}
          </DialogTitle>
        </DialogHeader>

        {items.length === 0 ? (
          <div className="text-center py-8 text-slate-600">
            {type === "lowStock" ? "No low stock items" : "No expiring items"}
          </div>
        ) : type === "lowStock" ? (
          renderLowStockContent()
        ) : (
          renderExpirationContent()
        )}
      </DialogContent>
    </Dialog>
  );
}
